/**
 * Badge primitive — restyled for the Abyss visual identity (Phase 2).
 *
 * Variants now map directly to Abyss semantic tokens (ok/warn/bad/accent/neutral).
 * The `live` pulsing dot uses the shared `status-pulse` keyframe from tokens.css.
 * No API change — existing `variant` / `live` / `children` usage keeps compiling.
 */
import { type HTMLAttributes } from 'react';
import clsx from 'clsx';
import { useMotionPreference } from '../lib/motion';

export type BadgeVariant = 'ok' | 'warn' | 'danger' | 'info' | 'neutral';

const VARIANT_CLASSES: Record<BadgeVariant, string> = {
  ok:      'bg-ok/15 text-ok border-ok/40',
  warn:    'bg-warn/15 text-warn border-warn/40',
  danger:  'bg-bad/15 text-bad border-bad/40',
  info:    'bg-accent-cyan/15 text-accent-cyan border-accent-cyan/40',
  neutral: 'bg-surface-glass text-text-2 border-surface-stroke',
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  /** Adds a soft pulsing dot to suggest "live" — uses shared status-pulse keyframe. */
  live?: boolean;
}

export function Badge({
  className,
  variant = 'neutral',
  live = false,
  children,
  ...props
}: BadgeProps) {
  const pref = useMotionPreference();
  const shouldPulse = live && pref === 'full';

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium',
        VARIANT_CLASSES[variant],
        className,
      )}
      {...props}
    >
      {live ? (
        <span
          className="inline-block h-1.5 w-1.5 rounded-full bg-current"
          style={
            shouldPulse
              ? { animation: 'status-pulse 2s ease-in-out infinite' }
              : undefined
          }
        />
      ) : null}
      {children}
    </span>
  );
}
